import { useCallback, useMemo } from 'react';
import { EngineService } from '@/services/engine';
import { AlertRuleEvent } from '@/types/event';
import { PaginatedResponse } from '@/types/responses';
import { useQueryFiltersStore } from '@/stores';
import { useSortFromUrl } from '@/hooks';
import { SortOption } from '@/utils/query-filters';
import {
    EVENTS_PAGE_SIZE,
    resolveListFetchOrderBy,
    sortOptionToOrderBy,
    useBaseEventFilters,
} from './eventsDataShared';

export interface UseAlertsQueryOptions {
    sourceId?: string;
    alertRuleId?: string;
    pageKey?: string;
}

export interface AlertsQuery {
    cacheKey: string;
    pageStateKey?: string;
    fetchPage: (
        pageIndex: number,
    ) => Promise<PaginatedResponse<AlertRuleEvent>>;
}

export function useAlertsQuery({
    sourceId,
    alertRuleId,
    pageKey,
}: UseAlertsQueryOptions): AlertsQuery {
    const filters = useQueryFiltersStore((state) => state.filters);
    const [sortOption] = useSortFromUrl(SortOption.DATE_NEWEST);
    const selectedAlertRuleIds = useQueryFiltersStore(
        (state) => state.selectedAlertRuleIds,
    );
    const baseParams = useBaseEventFilters(sourceId);

    const filterParams = useMemo(() => {
        const params: {
            sourceId?: string[];
            folderId?: string[];
            alertRuleId?: string[];
            minConfidence?: number;
            maxConfidence?: number;
            startTime?: number;
            endTime?: number;
        } = { ...baseParams };

        const selectedAlertRuleIdsArray = Array.from(selectedAlertRuleIds);
        if (selectedAlertRuleIdsArray.length > 0 && !alertRuleId) {
            params.alertRuleId = selectedAlertRuleIdsArray;
        }

        if (filters.confidence.enabled) {
            params.minConfidence = filters.confidence.min;
            params.maxConfidence = filters.confidence.max;
        }

        return params;
    }, [baseParams, alertRuleId, filters.confidence, selectedAlertRuleIds]);

    const orderBy = sortOptionToOrderBy(sortOption, true);

    const cacheKey = useMemo(() => {
        const parts: string[] = ['alerts'];
        if (sourceId) parts.push(`source-${sourceId}`);
        if (alertRuleId) parts.push(`alert-${alertRuleId}`);
        parts.push(JSON.stringify(filterParams));
        parts.push(orderBy);
        return parts.join('-');
    }, [sourceId, alertRuleId, filterParams, orderBy]);
    const pageStateKey = pageKey ? `${pageKey}:${cacheKey}` : undefined;

    const fetchPage = useCallback(
        async (
            pageIndex: number,
        ): Promise<PaginatedResponse<AlertRuleEvent>> => {
            const { effectiveOrderBy } = resolveListFetchOrderBy(
                filterParams,
                orderBy,
            );

            const baseParams = {
                page: pageIndex + 1,
                size: EVENTS_PAGE_SIZE,
                orderBy: effectiveOrderBy,
            };

            if (alertRuleId) {
                return EngineService.getAlertRuleAlerts(alertRuleId, {
                    ...baseParams,
                    ...filterParams,
                });
            }

            return EngineService.getAlertRuleEvents({
                ...baseParams,
                ...filterParams,
            });
        },
        [alertRuleId, filterParams, orderBy],
    );

    return { cacheKey, pageStateKey, fetchPage };
}
